// Group Events: Webinars, group sessions, multi-attendee bookings
import React, { useState, useEffect } from 'react';
import { schedulingApi } from '../../services/scheduling-api';
import './SchedulingStyles.css';

interface GroupEvent {
  id: number;
  title: string;
  slug: string;
  description?: string;
  durationMinutes: number;
  maxAttendees: number;
  minAttendees: number;
  showAttendeeCount: boolean;
  isActive: boolean;
  color: string;
}

interface Session {
  startTime: string;
  endTime: string;
  attendees: { id: number; name: string; email: string }[];
  spotsRemaining: number;
}

interface GroupEventFormData {
  title: string;
  description: string;
  durationMinutes: number;
  maxAttendees: number;
  minAttendees: number;
  showAttendeeCount: boolean;
  locationType: string;
  color: string;
}

export const GroupEvents: React.FC = () => {
  const [groupEvents, setGroupEvents] = useState<GroupEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<GroupEvent | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState<GroupEventFormData>({
    title: '',
    description: '',
    durationMinutes: 60,
    maxAttendees: 10,
    minAttendees: 1,
    showAttendeeCount: true,
    locationType: 'google_meet',
    color: '#8b5cf6'
  });

  const [newSessionTimes, setNewSessionTimes] = useState<string[]>(['']);

  useEffect(() => {
    fetchGroupEvents();
  }, []);

  const fetchGroupEvents = async () => {
    try {
      const response = await schedulingApi.get('/group/events');
      setGroupEvents(response.data.groupEvents || []);
    } catch (err) {
      console.error('Failed to fetch group events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async (eventId: number) => {
    try {
      const response = await schedulingApi.get(`/group/sessions/${eventId}`);
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      await schedulingApi.post('/group/events', formData);
      setSuccess('Group event created!');
      setShowCreateModal(false);
      resetForm();
      fetchGroupEvents();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create group event');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      durationMinutes: 60,
      maxAttendees: 10,
      minAttendees: 1,
      showAttendeeCount: true,
      locationType: 'google_meet',
      color: '#8b5cf6'
    });
  };

  const handleViewSessions = async (event: GroupEvent) => {
    setSelectedEvent(event);
    await fetchSessions(event.id);
    setShowSessionsModal(true);
  };

  const handleCreateSessions = async () => {
    if (!selectedEvent) return;

    const validTimes = newSessionTimes.filter(t => t);
    if (validTimes.length === 0) {
      setError('Add at least one session time');
      return;
    }

    try {
      await schedulingApi.post('/group/sessions', {
        eventTypeId: selectedEvent.id,
        startTimes: validTimes.map(t => new Date(t).toISOString())
      });
      setSuccess(`Created ${validTimes.length} session(s)`);
      setNewSessionTimes(['']);
      fetchSessions(selectedEvent.id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create sessions');
    }
  };

  const addSessionTimeField = () => {
    setNewSessionTimes([...newSessionTimes, '']);
  };

  const updateSessionTime = (index: number, value: string) => {
    const updated = [...newSessionTimes];
    updated[index] = value;
    setNewSessionTimes(updated);
  };

  const removeSessionTimeField = (index: number) => {
    setNewSessionTimes(newSessionTimes.filter((_, i) => i !== index));
  };

  const formatDateTime = (iso: string) => {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const colorOptions = [
    { value: '#8b5cf6', label: 'Purple' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#10b981', label: 'Green' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#ef4444', label: 'Red' },
    { value: '#ec4899', label: 'Pink' }
  ];

  if (loading) {
    return <div className="scheduling-loading">Loading group events...</div>;
  }

  return (
    <div className="scheduling-section">
      <div className="scheduling-header">
        <h2>Group Events</h2>
        <p>Host webinars, workshops, and group sessions with multiple attendees</p>
        <button
          className="scheduling-btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create Group Event
        </button>
      </div>

      {error && (
        <div className="scheduling-alert scheduling-alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="scheduling-alert scheduling-alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Group Events List */}
      <div className="scheduling-list">
        {groupEvents.length === 0 ? (
          <div className="scheduling-empty">
            <p>No group events created yet</p>
            <p>Create a group event to host webinars or workshops</p>
          </div>
        ) : (
          groupEvents.map(event => (
            <div key={event.id} className="scheduling-card">
              <div
                className="scheduling-card-color-bar"
                style={{ backgroundColor: event.color }}
              />
              <div className="scheduling-card-header">
                <h3>{event.title}</h3>
                <span className={`scheduling-status ${event.isActive ? 'active' : 'inactive'}`}>
                  {event.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {event.description && (
                <p className="scheduling-card-description">{event.description}</p>
              )}

              <div className="scheduling-card-meta">
                <span>{event.durationMinutes} min</span>
                <span>Max {event.maxAttendees} attendees</span>
                {event.minAttendees > 1 && (
                  <span>Min {event.minAttendees} required</span>
                )}
              </div>

              <div className="scheduling-card-actions">
                <button
                  className="scheduling-btn-secondary"
                  onClick={() => handleViewSessions(event)}
                >
                  View Sessions
                </button>
                <button
                  className="scheduling-btn-primary"
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowSessionsModal(true);
                    fetchSessions(event.id);
                  }}
                >
                  Schedule Session
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Group Event Modal */}
      {showCreateModal && (
        <div className="scheduling-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="scheduling-modal" onClick={e => e.stopPropagation()}>
            <div className="scheduling-modal-header">
              <h2>Create Group Event</h2>
              <button
                className="scheduling-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateEvent}>
              <div className="scheduling-form-group">
                <label>Event Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Workshop, Webinar, Group Training..."
                />
              </div>

              <div className="scheduling-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what attendees will learn or experience..."
                  rows={3}
                />
              </div>

              <div className="scheduling-form-row">
                <div className="scheduling-form-group">
                  <label>Duration</label>
                  <select
                    value={formData.durationMinutes}
                    onChange={e => setFormData({ ...formData, durationMinutes: Number(e.target.value) })}
                  >
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                    <option value={180}>3 hours</option>
                  </select>
                </div>

                <div className="scheduling-form-group">
                  <label>Location</label>
                  <select
                    value={formData.locationType}
                    onChange={e => setFormData({ ...formData, locationType: e.target.value })}
                  >
                    <option value="google_meet">Google Meet</option>
                    <option value="zoom">Zoom</option>
                    <option value="teams">Microsoft Teams</option>
                    <option value="in_person">In Person</option>
                    <option value="custom">Custom Link</option>
                  </select>
                </div>
              </div>

              <div className="scheduling-form-row">
                <div className="scheduling-form-group">
                  <label>Max Attendees</label>
                  <input
                    type="number"
                    min={2}
                    max={500}
                    value={formData.maxAttendees}
                    onChange={e => setFormData({ ...formData, maxAttendees: Number(e.target.value) })}
                  />
                </div>

                <div className="scheduling-form-group">
                  <label>Min Attendees</label>
                  <input
                    type="number"
                    min={1}
                    max={formData.maxAttendees}
                    value={formData.minAttendees}
                    onChange={e => setFormData({ ...formData, minAttendees: Number(e.target.value) })}
                  />
                  <small>Minimum needed to run the event</small>
                </div>
              </div>

              <div className="scheduling-form-group">
                <label>Event Color</label>
                <div className="scheduling-color-picker">
                  {colorOptions.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`scheduling-color-option ${formData.color === color.value ? 'selected' : ''}`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              <div className="scheduling-form-group">
                <label className="scheduling-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.showAttendeeCount}
                    onChange={e => setFormData({ ...formData, showAttendeeCount: e.target.checked })}
                  />
                  Show remaining spots to attendees
                </label>
              </div>

              <div className="scheduling-modal-actions">
                <button
                  type="button"
                  className="scheduling-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="scheduling-btn-primary">
                  Create Event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sessions Modal */}
      {showSessionsModal && selectedEvent && (
        <div className="scheduling-modal-overlay" onClick={() => setShowSessionsModal(false)}>
          <div className="scheduling-modal scheduling-modal-large" onClick={e => e.stopPropagation()}>
            <div className="scheduling-modal-header">
              <h2>{selectedEvent.title} - Sessions</h2>
              <button
                className="scheduling-modal-close"
                onClick={() => setShowSessionsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="scheduling-sessions-content">
              {/* Add New Sessions */}
              <div className="scheduling-add-sessions">
                <h3>Schedule New Sessions</h3>
                {newSessionTimes.map((time, index) => (
                  <div key={index} className="scheduling-session-time-row">
                    <input
                      type="datetime-local"
                      value={time}
                      onChange={e => updateSessionTime(index, e.target.value)}
                    />
                    {newSessionTimes.length > 1 && (
                      <button
                        type="button"
                        className="scheduling-btn-icon"
                        onClick={() => removeSessionTimeField(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <div className="scheduling-session-actions">
                  <button
                    type="button"
                    className="scheduling-btn-secondary"
                    onClick={addSessionTimeField}
                  >
                    + Add Another Time
                  </button>
                  <button
                    type="button"
                    className="scheduling-btn-primary"
                    onClick={handleCreateSessions}
                  >
                    Create Sessions
                  </button>
                </div>
              </div>

              {/* Existing Sessions */}
              <div className="scheduling-existing-sessions">
                <h3>Upcoming Sessions</h3>
                {sessions.length === 0 ? (
                  <p className="scheduling-no-sessions">No sessions scheduled yet</p>
                ) : (
                  sessions.map((session, index) => (
                    <div key={index} className="scheduling-session-card">
                      <div className="scheduling-session-time">
                        {formatDateTime(session.startTime)}
                      </div>
                      <div className="scheduling-session-info">
                        <span className="scheduling-session-attendees">
                          {session.attendees.length} / {selectedEvent.maxAttendees} registered
                        </span>
                        <span className="scheduling-session-spots">
                          {session.spotsRemaining} spots remaining
                        </span>
                      </div>
                      {session.attendees.length > 0 && (
                        <div className="scheduling-attendee-list">
                          <strong>Attendees:</strong>
                          <ul>
                            {session.attendees.map(attendee => (
                              <li key={attendee.id}>
                                {attendee.name} ({attendee.email})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Group Event Registration Page
export const PublicGroupEventPage: React.FC<{ subdomain: string; slug: string }> = ({
  subdomain,
  slug
}) => {
  const [eventType, setEventType] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    inviteeName: '',
    inviteeEmail: '',
    inviteeTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchGroupSessions();
  }, [subdomain, slug]);

  const fetchGroupSessions = async () => {
    try {
      const response = await schedulingApi.get(`/group/public/${subdomain}/${slug}/sessions`);
      setEventType(response.data.eventType);
      setSessions(response.data.sessions || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSession) {
      setError('Please select a session');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await schedulingApi.post('/group/register', {
        subdomain,
        eventSlug: slug,
        startTime: selectedSession,
        inviteeName: formData.inviteeName,
        inviteeEmail: formData.inviteeEmail,
        inviteeTimezone: formData.inviteeTimezone
      });

      setSuccess('Registration confirmed! Check your email for details.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to register');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (iso: string) => {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="scheduling-loading">Loading event...</div>;
  }

  if (error && !eventType) {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-error-page">
          <h2>Event Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-success-page">
          <h2>You're Registered!</h2>
          <p>{success}</p>
          <p>We've sent a calendar invite to {formData.inviteeEmail}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scheduling-public-page">
      <div className="scheduling-group-container">
        <div className="scheduling-group-header">
          <h1>{eventType.title}</h1>
          {eventType.description && <p>{eventType.description}</p>}
          <div className="scheduling-group-meta">
            <span>{eventType.durationMinutes} min</span>
          </div>
        </div>

        {error && (
          <div className="scheduling-alert scheduling-alert-error">{error}</div>
        )}

        <form onSubmit={handleRegister}>
          <h3>Select a Session</h3>

          {sessions.length === 0 ? (
            <p className="scheduling-no-sessions">No sessions available at this time.</p>
          ) : (
            <div className="scheduling-session-options">
              {sessions.map((session, index) => (
                <label
                  key={index}
                  className={`scheduling-session-option ${selectedSession === session.startTime ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="session"
                    value={session.startTime}
                    checked={selectedSession === session.startTime}
                    onChange={() => setSelectedSession(session.startTime)}
                  />
                  <div className="scheduling-session-details">
                    <span className="scheduling-session-date">
                      {formatDateTime(session.startTime)}
                    </span>
                    {session.spotsRemaining !== null && (
                      <span className="scheduling-session-spots">
                        {session.spotsRemaining} spots left
                      </span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {sessions.length > 0 && (
            <>
              <h3>Your Information</h3>

              <div className="scheduling-form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.inviteeName}
                  onChange={e => setFormData({ ...formData, inviteeName: e.target.value })}
                  required
                />
              </div>

              <div className="scheduling-form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.inviteeEmail}
                  onChange={e => setFormData({ ...formData, inviteeEmail: e.target.value })}
                  required
                />
              </div>

              <button
                type="submit"
                className="scheduling-btn-primary scheduling-btn-full"
                disabled={submitting || !selectedSession}
              >
                {submitting ? 'Registering...' : 'Register for Session'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default GroupEvents;
